import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCurrency, getSession, type Agreement } from '../lib/session.ts';
import { postJson } from '../lib/api.ts';
import { getConsumer } from '../lib/cable.ts';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const session = getSession();
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWaitingPayment, setIsWaitingPayment] = useState(true);
  const [paymentLabel, setPaymentLabel] = useState('');
  const [payload, setPayload] = useState('');
  const [base64, setBase64] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  // O termo já congelado, para o paciente ler antes de pagar: ele assina este arquivo
  // depois, e pagar sem saber o que se assina seria o pior dos dois mundos.
  const [term, setTerm] = useState('');
  // Até quando o PIX vale — e o termo com ele (ver Agreement#payment_expires_at).
  const [expiresAt, setExpiresAt] = useState('');
  const copyTimeoutRef: { current: number | null } = { current: null };
  const fetchedQrcodeKeyRef = useRef<string | null>(null);

  const handlePayment = async () => {
    if (!code) return;

    setIsSubmitting(true);
    setError('');
    setInfo('');

    try {
      navigate(`/agreement/${session?.agreementCode}/term`);
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error(error);
      setError('Falha ao copiar para a área de transferência.');
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await copyToClipboard(text);
      setIsCopied(true);
      setInfo('Código pix copiado');
      if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = window.setTimeout(() => {
        setIsCopied(false);
        copyTimeoutRef.current = null;
        setInfo('');
      }, 10000);
    } catch (error) {
      console.error(error);
      setError('Falha ao copiar para a área de transferência e notificar.');
    }
  };

  useEffect(() => {
    if (!session || !session.otp || !session?.agreementCode || !code) {
      navigate('/identification');
      return;
    }

    if (!session?.paymentAmount || !session?.agreement) {
      navigate(`/agreement/${session?.agreementCode}/simulation`);
      return;
    }

    // À vista é o acordo SEM parcelas (installments 0, como a simulação envia — ver
    // AgreementTermPdf#installments?). Com `> 1`, entrada + uma parcela saía rotulada como
    // "à vista", e o paciente pagava achando que quitava tudo.
    const installments = session?.agreement?.installments || 0;
    setPaymentLabel(installments > 0 ? 'Valor da entrada' : 'Valor total à vista');

    if (fetchedQrcodeKeyRef.current === session?.agreementCode) return;
    fetchedQrcodeKeyRef.current = session?.agreementCode;

    const fetchQrcode = async () => {
      try {
        const result: any = await postJson<string>(`/api/agreement/${code}/payment`, {});
        setPayload(result.payload);
        setBase64(result.base64);
        setExpiresAt(result.expires_at || '');
      } catch (error) {
        console.error(error);
        setError((error as Error).message || 'Erro ao registrar acordo e recuperar o qrcode.');
      }
    };

    // O termo vem primeiro: com a entrada já paga (o paciente voltou à tela), o que falta é
    // assinar, e pedir o qrcode ali daria a ele uma segunda cobrança para pagar.
    const load = async () => {
      try {
        const agreement = await postJson<Agreement>(`/api/agreement/${code}/term`, {});
        setTerm(agreement.term || '');
        if (agreement.paid) {
          navigate(`/agreement/${code}/term`);
          return;
        }
      } catch (error) {
        console.error(error);
      }

      await fetchQrcode();
    };

    load();
  }, [session?.agreementCode, code]);

  useEffect(() => {
    if (!code) return;

    const subscription = getConsumer().subscriptions.create(
      { channel: 'PaymentChannel', agreement_code: code },
      {
        received: (data: { status?: string }) => {
          if (data.status === 'paid') {
            setIsWaitingPayment(false);
            setInfo('Pagamento confirmado! Agora falta assinar o termo.');
            navigate(`/agreement/${code}/term`);
          }
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [code]);

  if (!session) return null;

  const agreement = session.agreement;
  const upfront = !agreement?.installments;
  // O horário é o de Brasília, que é onde o prazo é contado (Agreement::TIME_ZONE) — e não
  // o do aparelho, que pode estar em outro fuso.
  const deadline = expiresAt
    ? new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'America/Sao_Paulo',
      }).format(new Date(expiresAt))
    : '';

  return (
    <main className="space-y-8">
      <section className="rounded-3xl bg-white p-8 shadow-md shadow-slate-200">
        <p className="text-sm uppercase tracking-[0.3em] text-red-600">
          {upfront ? 'Pagamento à vista' : 'Pagamento da entrada'}
        </p>
        <h1 className="mt-4 text-3xl font-semibold text-red-600">Acordo {code}</h1>
        <p className="mt-3 text-slate-600">
          Antes de pagar, leia o termo do acordo: ele traz o valor total, as parcelas e as datas
          de vencimento.{' '}
          <strong>
            Ao pagar {upfront ? 'o valor à vista' : 'a entrada'} de {formatCurrency(session?.paymentAmount || 0)},
            você confirma que concorda com essas condições.
          </strong>{' '}
          Assim que o pagamento for confirmado, falta só assinar o termo para concluir o acordo.
        </p>
        {deadline && (
          <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            O PIX vale até <strong>{deadline}</strong>. Depois disso, o termo perde a validade e é
            preciso gerar um novo.
          </p>
        )}
      </section>

      {/* O resumo vem ANTES do qrcode e antes do PDF: no celular o PDF dentro do iframe costuma
          sair em branco (ou virar download), e o paciente precisa ver o que está aceitando
          mesmo quando o termo não abre na tela. */}
      <section className="rounded-3xl bg-red-50 p-8 shadow-sm">
        <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Condições do acordo</p>
        <dl className="mt-4 space-y-2 text-slate-700">
          {upfront ? (
            <div className="flex justify-between gap-4">
              <dt>Pagamento à vista</dt>
              <dd className="font-semibold">{formatCurrency(session?.paymentAmount || 0)}</dd>
            </div>
          ) : (
            <>
              <div className="flex justify-between gap-4">
                <dt>Entrada (hoje)</dt>
                <dd className="font-semibold">{formatCurrency(session?.paymentAmount || 0)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt>Parcelas</dt>
                <dd className="font-semibold">
                  {agreement?.installments}x {formatCurrency(agreement?.installment_amount || 0)}
                </dd>
              </div>
              {agreement?.due_day ? (
                <div className="flex justify-between gap-4">
                  <dt>Vencimento das parcelas</dt>
                  <dd className="font-semibold">todo dia {agreement.due_day}</dd>
                </div>
              ) : null}
            </>
          )}
        </dl>

        {term && (
          <div className="mt-6 space-y-4">
            <a
              href={term}
              download={`termo-acordo-${code}.pdf`}
              className="block w-full rounded-2xl border border-red-200 bg-white px-5 py-3 text-center text-sm font-semibold text-red-700 transition hover:bg-red-100"
            >
              Abrir o termo do acordo (PDF)
            </a>
            {/* Só no computador: no celular o botão acima é o caminho que funciona. */}
            <iframe
              src={term}
              title="Termo de acordo"
              className="hidden h-[600px] w-full rounded-3xl border border-slate-200 bg-white shadow-sm sm:block"
            />
          </div>
        )}
      </section>

      <section className="rounded-3xl bg-red-50 p-8 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.35em] text-slate-500">{paymentLabel}</p>
            <p className="mt-3 text-3xl font-semibold text-slate-600">{formatCurrency(session?.paymentAmount || 0)}</p>
          </div>
        </div>

        <div className="mt-8 rounded-3xl bg-white p-6 shadow-sm">
          <p className="mb-3 text-sm uppercase tracking-[0.35em] text-slate-500">QRCode</p>
          <div className="rounded-3xl border border-slate-200 bg-red-50 p-4">
            {base64 ? (
              <img
                src={base64}
                alt="QR Code Pix"
                className="mx-auto max-h-72"
              />
            ) : (
              <p className="text-sm text-slate-500">Gerando QR Code...</p>
            )}
          </div>

          <div className="mt-8 rounded-3xl border border-slate-200 bg-red-50 p-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Pix copia e cola</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleCopy(payload)}
                  disabled={!payload}
                  className={`rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${isCopied ? 'bg-green-50 text-green-700' : 'bg-white text-slate-700 hover:bg-red-100'}`}
                >
                  {isCopied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>
            <textarea
              readOnly
              value={payload}
              className="mt-4 min-h-35 w-full resize-none rounded-3xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm"
            />
          </div>
        </div>
      </section>

      {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {info && <div className="rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-700">{info}</div>}
      <button
        onClick={handlePayment}
        disabled={isSubmitting || isWaitingPayment}
        className="w-full rounded-2xl bg-red-600 text-white px-5 py-4 text-sm font-semibold shadow-lg transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isWaitingPayment
          ? 'Aguardando confirmação do pagamento...'
          : isSubmitting
            ? 'Avançando...'
            : 'Pagamento confirmado, assinar o termo'}
      </button>
    </main>
  );
};

export default PaymentPage;
